import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteModule
} from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { AbilityService } from 'src/app/core/services/ability/ability.service';
import { ReferringDoctorService } from '../../services/referring-doctors.service';
import { AddReferringDoctorsDialogComponent } from '../add-referring-doctors-dialog/add-referring-doctors-dialog.component';

interface ReferringDoctor {
  value: string;
  viewValue: string;
  phone?: string;
  fax?: string;
}

/** Doctors fetched per request; more are appended as the panel is scrolled. */
const PAGE_SIZE = 10;

/** How close to the bottom of the panel counts as "scrolled to the end". */
const SCROLL_THRESHOLD_PX = 48;

/** Idle time after the last keystroke before a search is sent. */
const SEARCH_DEBOUNCE_MS = 300;

/** Ids arrive as numbers from the API and as strings from form controls. */
function idOf(value: unknown): string {
  return value === null || value === undefined ? '' : String(value).trim();
}

@Component({
  selector: 'app-referring-doctors-select',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    FormsModule,
    MatInputModule,
    MatIconModule,
    MatAutocompleteModule,
  ],
  templateUrl: './referring-doctors-select.component.html',
  styleUrls: ['./referring-doctors-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ReferringDoctorSelectComponent),
      multi: true,
    },
  ],
})
export class ReferringDoctorSelectComponent
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy
{
  /** Options currently offered by the panel, grown page by page. */
  filteredDoctors: ReferringDoctor[] = [];
  loading = false;
  @Output() departmentSelected = new EventEmitter<string>();
  /** The whole option, for callers that also need its phone/fax. */
  @Output() doctorSelected = new EventEmitter<ReferringDoctor | null>();
  @Input() selectedValue: string | null = null;
  @Input() required?: boolean = false;
  @Input() disabled?: boolean = false;
  selectedDoctor: ReferringDoctor | null = null;

  @ViewChild('auto') private autocomplete?: MatAutocomplete;

  constructor(
    public ability: AbilityService,
    private referringDoctorService: ReferringDoctorService,
    private dialog: MatDialog
  ) {}

  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  private readonly searchTerms = new Subject<string>();
  private readonly destroyed = new Subject<void>();
  private query = '';
  private page = 0;
  private totalPages = 1;
  /** Guards against a slow earlier response overwriting a newer one. */
  private latestRequestId = 0;
  /** Id currently being resolved by name, so it is only fetched once. */
  private resolvingId: string | null = null;

  ngOnInit() {
    this.searchTerms
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        takeUntil(this.destroyed)
      )
      .subscribe((term) => this.runSearch(term));

    this.runSearch('');
    this.syncSelectedDoctor();
  }

  ngOnChanges(changes: SimpleChanges) {
    // The id often arrives after this component is created — a patient being
    // edited is fetched asynchronously — so react to it whenever it lands.
    if (changes['selectedValue']) {
      this.syncSelectedDoctor();
    }
  }

  ngOnDestroy() {
    this.detachPanelScrollListener();
    this.destroyed.next();
    this.destroyed.complete();
  }

  writeValue(value: string): void {
    this.selectedValue = value;
    this.syncSelectedDoctor();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /** Typing filters server-side; picking an option hands back the object. */
  filterDoctors(event: any) {
    if (typeof event !== 'string') return;
    this.searchTerms.next(event.trim());
  }

  onSelectReferringDoctor(doctor: ReferringDoctor) {
    if (!doctor) return;
    this.selectedDoctor = doctor;
    this.selectedValue = doctor.value;
    this.onChange(doctor.value);
    this.departmentSelected.emit(doctor.value);
    this.doctorSelected.emit(doctor);
  }

  displayDoctor(doctor: ReferringDoctor): string {
    return doctor?.viewValue || '';
  }

  openAddReferringDoctorDialog() {
    this.dialog
      .open(AddReferringDoctorsDialogComponent, {
        width: '400px',
      })
      .afterClosed()
      .subscribe((res) => {
        // Dismissing the dialog resolves without a doctor.
        if (!res?.id) return;
        const doctor: ReferringDoctor = {
          viewValue: res.name,
          value: idOf(res.id),
          phone: res.phone,
          fax: res.fax,
        };
        // Offer it straight away; the panel only holds the pages fetched so far.
        this.filteredDoctors = [doctor, ...this.filteredDoctors];
        this.onSelectReferringDoctor(doctor);
      });
  }

  /** Angular Material renders the panel lazily, so bind on each open. */
  onPanelOpened() {
    setTimeout(() => this.attachPanelScrollListener());
  }

  private get panelElement(): HTMLElement | null {
    return this.autocomplete?.panel?.nativeElement ?? null;
  }

  private attachPanelScrollListener() {
    const panel = this.panelElement;
    if (!panel) return;
    panel.removeEventListener('scroll', this.onPanelScroll);
    panel.addEventListener('scroll', this.onPanelScroll);
  }

  private detachPanelScrollListener() {
    this.panelElement?.removeEventListener('scroll', this.onPanelScroll);
  }

  private onPanelScroll = () => {
    const panel = this.panelElement;
    if (!panel) return;
    const distanceToBottom =
      panel.scrollHeight - (panel.scrollTop + panel.clientHeight);
    if (distanceToBottom <= SCROLL_THRESHOLD_PX) {
      this.loadNextPage();
    }
  };

  private runSearch(term: string) {
    this.query = term;
    this.page = 0;
    this.totalPages = 1;
    this.fetchNextPage(true);
  }

  private loadNextPage() {
    if (this.loading || this.page >= this.totalPages) return;
    this.fetchNextPage(false);
  }

  private fetchNextPage(replace: boolean) {
    const page = replace ? 1 : this.page + 1;
    const query: any = { page, limit: PAGE_SIZE };
    if (this.query) query.q = this.query;

    this.loading = true;
    const requestId = ++this.latestRequestId;

    this.referringDoctorService
      .getReferringDoctor(query)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (resp) => {
          // A newer search has already been issued; this response is stale.
          if (requestId !== this.latestRequestId) return;
          this.loading = false;
          this.page = Number(resp?.meta?.currentPage) || page;
          this.totalPages = Number(resp?.meta?.totalPages) || 1;

          const options: ReferringDoctor[] = (resp?.items ?? []).map((m) => ({
            viewValue: m.name,
            value: idOf(m.id),
            phone: m.phone,
            fax: m.fax,
          }));

          this.filteredDoctors = replace
            ? [...this.leadingOptions(), ...options]
            : this.appendUnseen(this.filteredDoctors, options);

          this.syncSelectedDoctor();
        },
        error: (error) => {
          if (requestId !== this.latestRequestId) return;
          this.loading = false;
          console.error('Error loading referringDoctors', error);
        },
      });
  }

  /** The "All" reset entry, offered only on an unfiltered optional picker. */
  private leadingOptions(): ReferringDoctor[] {
    return !this.required && !this.query
      ? [{ viewValue: 'All', value: '' }]
      : [];
  }

  /**
   * Appends by id, so the selected doctor pulled in ahead of its page is not
   * listed twice while genuinely distinct same-name doctors both survive.
   */
  private appendUnseen(
    existing: ReferringDoctor[],
    incoming: ReferringDoctor[]
  ): ReferringDoctor[] {
    const seen = new Set(existing.map((d) => d.value));
    return [...existing, ...incoming.filter((d) => !seen.has(d.value))];
  }

  /**
   * Makes the field show the name behind `selectedValue`. The doctor is usually
   * absent from the loaded pages, so fall back to fetching it by id — that is
   * what keeps an existing selection visible when a record is opened for edit.
   */
  private syncSelectedDoctor() {
    const id = idOf(this.selectedValue);

    if (!id) {
      this.selectedDoctor = null;
      this.resolvingId = null;
      return;
    }

    if (idOf(this.selectedDoctor?.value) === id) return;

    const loaded = this.filteredDoctors.find((d) => d.value === id);
    if (loaded) {
      this.selectedDoctor = loaded;
      return;
    }

    this.resolveDoctorById(id);
  }

  private resolveDoctorById(id: string) {
    if (this.resolvingId === id) return;
    this.resolvingId = id;

    this.referringDoctorService
      .getReferringDoctorById(id)
      .pipe(takeUntil(this.destroyed))
      .subscribe({
        next: (m) => {
          this.resolvingId = null;
          if (!m?.id) return;
          // The binding may have moved on while this was in flight.
          if (idOf(this.selectedValue) !== idOf(m.id)) return;

          const doctor: ReferringDoctor = {
            viewValue: m.name,
            value: idOf(m.id),
            phone: m.phone,
            fax: m.fax,
          };
          this.selectedDoctor = doctor;
          this.filteredDoctors = this.appendUnseen([doctor], this.filteredDoctors);
        },
        error: () => {
          this.resolvingId = null;
        },
      });
  }
}
